import {useCallback, useEffect, useRef} from 'react';

type UseMaxInstructionHeightParams = {
    enabled: boolean;
    itemCount: number;
    onChange?: (height: number) => void;
};

export const useMaxInstructionHeight = ({
    enabled,
    itemCount,
    onChange,
}: UseMaxInstructionHeightParams) => {
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const observerRef = useRef<ResizeObserver | null>(null);
    const refMap = useRef(new Map<number, (node: HTMLDivElement | null) => void>());
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const reportMaxHeight = useCallback(() => {
        if (!enabled) return;

        const maxHeight = itemRefs.current.reduce((max, node) => {
            const height = node?.getBoundingClientRect().height ?? 0;
            return height > max ? height : max;
        }, 0);

        onChangeRef.current?.(maxHeight);
    }, [enabled]);

    const reportMaxHeightRef = useRef(reportMaxHeight);
    reportMaxHeightRef.current = reportMaxHeight;

    const setItemRef = useCallback(
        (index: number) => {
            const existingRef = refMap.current.get(index);
            if (existingRef) return existingRef;

            const itemRef = (node: HTMLDivElement | null) => {
                const prev = itemRefs.current[index];
                itemRefs.current[index] = node;

                const observer = observerRef.current;
                if (prev && observer) {
                    observer.unobserve(prev);
                }
                if (node && observer) {
                    observer.observe(node);
                    reportMaxHeightRef.current();
                }
            };

            refMap.current.set(index, itemRef);
            return itemRef;
        },
        []
    );

    useEffect(() => {
        if (!enabled) {
            observerRef.current?.disconnect();
            observerRef.current = null;
            return;
        }

        itemRefs.current.length = itemCount;
        [...refMap.current.keys()].forEach((index) => {
            if (index >= itemCount) refMap.current.delete(index);
        });

        const observer = new ResizeObserver(() => {
            reportMaxHeightRef.current();
        });
        observerRef.current = observer;

        itemRefs.current.forEach((node) => {
            if (node) observer.observe(node);
        });
        reportMaxHeightRef.current();

        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, [enabled, itemCount]);

    return {setItemRef};
};
